import { memo, ReactNode, useCallback, useEffect } from 'react';

import { Button } from '@hh.ru/magritte-ui';
import { CartOutlinedSize16 } from '@hh.ru/magritte-ui/icon';
import Column from 'bloko/blocks/column';
import Modal, { ModalFooter, ModalHeader, ModalTitle, ModalContent } from 'bloko/blocks/modal';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import ActiveOverlay from 'HHC/ActiveOverlay';
import { useChatikFloatActivatorShift } from 'src/components/FloatChatikActivator/useChatikFloatActivatorShift';
import FormatMoney from 'src/components/FormatMoney';
import translation from 'src/components/translation';
import { useOnOffState } from 'src/hooks/useOnOffState';
import { useSelectorNonNullable } from 'src/hooks/useSelector';
import { NON_BREAKING_SPACE } from 'src/utils/constants/symbols';

import PriceCartContentSwitcher from 'src/components/Price/PriceCart/PriceCartContentSwitcher';
import PriceCartFooter from 'src/components/Price/PriceCart/PriceCartFooter';

const TrlKeys = {
    title: 'employer.price.cart.title',
    totalPrice: 'employer.price.cart.sticky-total',
    summaryCheckout: 'employer.price.cart.see-cart',
};

const PriceCartSummary: TranslatedComponent = ({ trls }) => {
    const { currency, products, totalPrice } = useSelectorNonNullable(({ priceCart }) => priceCart);
    const [isModalVisible, showModal, hideModal] = useOnOffState(false);

    // tempexp_PORTFOLIO-38480_next_line
    const { stickyTrackingRef } = useChatikFloatActivatorShift<HTMLDivElement>();

    const initEventListener = useCallback((element: HTMLSpanElement) => {
        if (element) {
            element.addEventListener('animationend', () => {
                element.classList.remove('price-cart-update-blink');
            });
        }
    }, []);

    const renderTotalCost = useCallback(
        (amount: string, currencyNode: ReactNode) => (
            <>
                <span
                    className="HH-AddItemToCartAnimation-TotalCost"
                    data-qa="price-cart-summary-cost"
                    ref={initEventListener}
                >
                    {amount}
                </span>
                {NON_BREAKING_SPACE}
                {currencyNode}
            </>
        ),
        [initEventListener]
    );

    // Скрывает «Чаты с соискателями»
    useEffect(() => {
        ActiveOverlay.overlayOpened();
        return () => {
            ActiveOverlay.overlayClosed();
        };
    }, []);

    if (products.length === 0) {
        if (isModalVisible) {
            hideModal();
        }
        return null;
    }

    return (
        <>
            <Modal visible={isModalVisible} onClose={hideModal}>
                <ModalHeader>
                    <ModalTitle>{trls[TrlKeys.title]}</ModalTitle>
                </ModalHeader>
                <ModalContent>
                    <div className="price-cart-popup">
                        <PriceCartContentSwitcher hideLastDivider={true} />
                    </div>
                </ModalContent>
                <ModalFooter alignmentLeft>
                    <PriceCartFooter />
                </ModalFooter>
            </Modal>
            <div className="price-cart-summary-shadow" />
            <div ref={stickyTrackingRef} className="price-cart-summary price-cart-summary_sticky">
                <Column xs="4" s="8" m="12" l="4">
                    <div className="price-cart-summary-content">
                        <span className="price-cart-summary-cost">
                            {trls[TrlKeys.totalPrice]}:{' '}
                            <span className="money">
                                <FormatMoney currency={currency} inCents={true} render={renderTotalCost}>
                                    {totalPrice}
                                </FormatMoney>
                            </span>
                        </span>
                        <Button
                            mode="primary"
                            size="small"
                            style="accent"
                            data-qa="go-to-cart"
                            onClick={showModal}
                            icon={<CartOutlinedSize16 />}
                        >
                            {trls[TrlKeys.summaryCheckout]}
                        </Button>
                    </div>
                </Column>
            </div>
        </>
    );
};

export default memo(translation(PriceCartSummary));
